import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const translations = {
  lv: {
    title: 'Novērtējiet mūsu apkalpošanu',
    placeholder: 'Jūsu komentārs (neobligāti)',
    submit: 'Nosūtīt',
    submitting: 'Sūta...',
    anonymous: 'Anonīms novērtējums',
    thanks: 'Paldies!',
    received: 'Jūsu atsauksme ir saņemta. Mēs novērtējam jūsu viedokli!',
    selectRating: 'Lūdzu, izvēlieties vērtējumu',
    error: 'Kļūda. Lūdzu, mēģiniet vēlreiz.'
  },
  en: {
    title: 'Rate our service',
    placeholder: 'Your comment (optional)',
    submit: 'Submit',
    submitting: 'Sending...',
    anonymous: 'Anonymous review',
    thanks: 'Thank you!',
    received: 'Your feedback has been received. We appreciate your opinion!',
    selectRating: 'Please select a rating',
    error: 'Error. Please try again.'
  },
  ru: {
    title: 'Оцените наш сервис',
    placeholder: 'Ваш комментарий (необязательно)',
    submit: 'Отправить',
    submitting: 'Отправка...',
    anonymous: 'Анонимная оценка',
    thanks: 'Спасибо!',
    received: 'Ваш отзыв получен. Мы ценим ваше мнение!',
    selectRating: 'Пожалуйста, выберите оценку',
    error: 'Ошибка. Попробуйте ещё раз.'
  }
};

const Review = () => {
  const [language, setLanguage] = useState('lv');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const t = translations[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error(t.selectRating);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment: comment || null })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="bg-white p-8 md:p-12 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-[#8B2942]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Star className="w-8 h-8 text-[#8B2942] fill-[#8B2942]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1D] mb-4">
            {t.thanks}
          </h1>
          <p className="text-[#6B6B6B]">
            {t.received}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
      <div className="bg-white p-8 md:p-12 rounded-lg shadow-lg max-w-md w-full">
        {/* Language Selector */}
        <div className="flex justify-center gap-2 mb-6">
          {['lv', 'en', 'ru'].map((lang) => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                language === lang
                  ? 'bg-[#8B2942] text-white'
                  : 'bg-gray-100 text-[#6B6B6B] hover:bg-gray-200'
              }`}
              data-testid={`lang-${lang}`}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1D]">
            PAVILLON <span className="font-light">de</span> ROZE
          </h1>
          <p className="text-[#8B2942] text-sm mt-2 tracking-widest">
            MODERN GEORGIAN CUISINE
          </p>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1D]">
            {t.title}
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Star Rating */}
          <div className="flex justify-center gap-2 mb-8">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`star-${star}`}
              >
                <Star
                  size={40}
                  className={`transition-colors ${
                    star <= (hoverRating || rating)
                      ? 'text-[#D4AF37] fill-[#D4AF37]'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Comment */}
          <div className="mb-6">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t.placeholder}
              className="w-full p-4 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B2942] resize-none h-32 text-[#1A1A1D]"
              data-testid="review-comment"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || rating === 0}
            className="w-full py-4 bg-[#8B2942] text-white font-medium rounded-lg hover:bg-[#6B1F32] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="review-submit"
          >
            {loading ? t.submitting : t.submit}
          </button>
        </form>

        <p className="text-center text-[#9CA3AF] text-xs mt-6">
          {t.anonymous}
        </p>
      </div>
    </div>
  );
};

export default Review;
